import React, { useState } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function TestimonialsCarousel({ 
  testimonials, 
  isLoaded = true, 
  title = "Client Testimonials", 
  subtitle = "Hear from our valued clients about their investment journey with us",
  backgroundColor = '#1F2937',
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // FAILSAFE: Don't render if:
  // 1. Data hasn't loaded yet (isLoaded is false)
  // 2. testimonials is null/undefined
  // 3. testimonials is empty array
  // 4. testimonials is not an array
  if (!isLoaded || !testimonials || !Array.isArray(testimonials) || testimonials.length === 0) {
    return null;
  }

  // Calculate how many to show based on screen (we'll show 3 on desktop, 1 on mobile)
  const itemsPerPage = typeof window !== 'undefined' && window.innerWidth >= 1024 ? 3 : 
                       typeof window !== 'undefined' && window.innerWidth >= 768 ? 2 : 1;
  
  const maxIndex = Math.max(0, testimonials.length - itemsPerPage);
  
  const handlePrev = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  };

  const visibleTestimonials = testimonials.slice(currentIndex, currentIndex + itemsPerPage);
  const showNavigation = testimonials.length > itemsPerPage;

  return (
    <section className="py-20" style={{backgroundColor}} data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-text-primary mb-4">
            {title}
          </h2>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="relative">
          {/* Navigation Arrows */}
          {showNavigation && (
            <>
              <button
                onClick={handlePrev}
                disabled={currentIndex === 0}
                className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 p-3 rounded-full glass-effect transition-all ${
                  currentIndex === 0 
                    ? 'opacity-30 cursor-not-allowed' 
                    : 'hover:bg-primary/20 hover:scale-110'
                }`}
                data-testid="testimonial-prev"
                aria-label="Previous testimonials"
              >
                <ChevronLeft className="h-6 w-6 text-text-primary" />
              </button>
              
              <button
                onClick={handleNext}
                disabled={currentIndex >= maxIndex}
                className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 p-3 rounded-full glass-effect transition-all ${
                  currentIndex >= maxIndex 
                    ? 'opacity-30 cursor-not-allowed' 
                    : 'hover:bg-primary/20 hover:scale-110'
                }`}
                data-testid="testimonial-next"
                aria-label="Next testimonials"
              >
                <ChevronRight className="h-6 w-6 text-text-primary" />
              </button>
            </>
          )}

          {/* Testimonials Grid */}
          <div className="overflow-hidden px-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {visibleTestimonials.map((testimonial, index) => (
                  <div
                    key={testimonial.id}
                    className="glass-effect p-8 rounded-2xl relative"
                    data-testid={`testimonial-card-${index}`}
                  >
                    <Quote className="h-10 w-10 text-primary/30 absolute top-6 right-6" />
                    
                    {/* Rating Stars */}
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                      ))}
                    </div>

                    {/* Content */}
                    <p className="text-text-secondary mb-6 leading-relaxed italic">
                      "{testimonial.content}"
                    </p>

                    {/* Client Info */}
                    <div className="border-t border-white/10 pt-4">
                      <p className="font-semibold text-text-primary">{testimonial.client_name}</p>
                      {(testimonial.designation || testimonial.company) && (
                        <p className="text-text-muted text-sm">
                          {testimonial.designation}{testimonial.designation && testimonial.company && ' at '}{testimonial.company}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Pagination Dots */}
          {showNavigation && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex 
                      ? 'bg-primary w-6' 
                      : 'bg-white/20 hover:bg-white/40'
                  }`}
                  aria-label={`Go to testimonial page ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
